import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gins } from '@/mocks/products';
import ProductCard from './ProductCard';
import useCarousel from '@/hooks/useCarousel';

const GinsSection = () => {
  const { t } = useTranslation();
  const { currentIndex, containerRef, trackStyle, itemStyle, prev, next, goTo, canPrev, canNext, dotsCount, pauseAutoplay, resumeAutoplay } = useCarousel({
    total: gins.length,
    visibleCards: 3,
    gap: 24,
    autoplay: true,
    autoplayInterval: 5000,
  });

  return (
    <section className="bg-[#0d0d0d] py-24 px-6 md:px-10 xl:px-16" data-product-shop>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <p className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase mb-3 font-medium">{t('gins_label')}</p>
            <h2
              className="text-4xl md:text-5xl font-light text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t('gins_title')}
            </h2>
            <p className="text-white/40 text-sm mt-3 max-w-md font-light">
              {t('gins_subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                disabled={!canPrev}
                className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Previous"
              >
                <i className="ri-arrow-left-line text-sm"></i>
              </button>
              <button
                onClick={next}
                disabled={!canNext}
                className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Next"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
            <Link
              to="/gins"
              className="flex items-center gap-2 border border-[#c9a84c]/40 hover:border-[#c9a84c] text-[#c9a84c] text-xs tracking-widest uppercase font-medium px-6 py-3 rounded-md transition-all whitespace-nowrap"
            >
              {t('gins_see_all')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
        >
          <div ref={containerRef} className="overflow-hidden">
            <div className="flex" style={trackStyle}>
              {gins.map(product => (
                <div key={product.id} style={itemStyle}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-10">
            {Array.from({ length: dotsCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === currentIndex ? 'w-8 h-1.5 bg-[#c9a84c]' : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="mt-14 relative rounded-2xl overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://readdy.ai/api/search-image?query=premium%20gin%20tonic%20cocktail%20glass%20botanical%20garnish%20elegant%20bar%20setting%20dark%20moody%20atmosphere%20luxury%20spirits%20professional%20photography%20ice%20citrus%20herbs&width=1400&height=400&seq=gin-banner&orientation=landscape')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase mb-3 font-medium">{t('gins_banner_label')}</p>
              <h3 className="text-3xl md:text-4xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t('gins_banner_title')}
              </h3>
              <p className="text-white/50 text-sm mt-3 max-w-sm font-light">
                {t('gins_banner_subtitle')}
              </p>
            </div>
            <Link
              to="/gins"
              className="bg-[#c9a84c] hover:bg-[#b8963e] text-black text-xs tracking-widest uppercase font-bold px-8 py-4 rounded-md transition-all whitespace-nowrap flex-shrink-0"
            >
              {t('gins_explore')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GinsSection;
